import { useEffect, useRef } from "react"
import { useMap } from "@vis.gl/react-google-maps"

import { type WeeklySchedule } from "@/services/firebase/functions"
import { colors } from "@/theme"

interface MarkerInfoWindowProps {
  position: {
    lat: number
    lng: number
  }
  title: string
  address: string
  onClose: () => void
  openingHours?: WeeklySchedule
  mapsUrl?: string
}

/** Groups consecutive days with same hours into structured lines */
function formatOpeningHoursLines(
  schedule: WeeklySchedule | null | undefined,
): Array<{ label: string; hours: string }> {
  if (!schedule) return [{ label: "Mo\u2013Fr", hours: "9:00\u201317:00" }]

  const days = [
    { name: "Mo", schedule: schedule.monday },
    { name: "Di", schedule: schedule.tuesday },
    { name: "Mi", schedule: schedule.wednesday },
    { name: "Do", schedule: schedule.thursday },
    { name: "Fr", schedule: schedule.friday },
    { name: "Sa", schedule: schedule.saturday },
    { name: "So", schedule: schedule.sunday },
  ]

  const groups: Array<{ days: string[]; hours: string }> = []
  let current: { days: string[]; hours: string } | null = null

  for (const day of days) {
    const s = day.schedule
    if (s?.isOpen && s.open && s.close) {
      const hours = `${s.open}\u2013${s.close}`
      if (current && current.hours === hours) {
        current.days.push(day.name)
      } else {
        if (current) groups.push(current)
        current = { days: [day.name], hours }
      }
    } else {
      if (current) {
        groups.push(current)
        current = null
      }
    }
  }
  if (current) groups.push(current)

  return groups.map((g) => ({
    label: g.days.length === 1 ? g.days[0] : `${g.days[0]}\u2013${g.days[g.days.length - 1]}`,
    hours: g.hours,
  }))
}

export const MarkerInfoWindow = ({
  position,
  title,
  address,
  onClose,
  openingHours,
  mapsUrl: mapsUrlProp,
}: MarkerInfoWindowProps) => {
  const map = useMap()
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null)

  useEffect(() => {
    if (!map) return

    // Use curated mapsUrl from Firestore or fall back to Google Maps search
    const mapsUrl =
      mapsUrlProp ||
      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent("HolDeinePlakette " + address)}`

    // Inject styles to override Google Maps InfoWindow chrome
    const styleId = "hdp-infowindow-style"
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style")
      style.id = styleId
      style.textContent = `
        .gm-style-iw-ch { display: none !important; }
        .gm-style-iw.gm-style-iw-c { padding: 0 !important; }
        .gm-style-iw-d { overflow: auto !important; }
        .hdp-maps-btn:hover { opacity: 0.88 !important; }
      `
      document.head.appendChild(style)
    }

    // --- Container ---
    const content = document.createElement("div")
    Object.assign(content.style, {
      fontFamily: "OverusedGrotesk, -apple-system, BlinkMacSystemFont, sans-serif",
      padding: "14px 16px 12px",
      minWidth: "200px",
      maxWidth: "250px",
    })

    // --- Title ---
    const titleEl = document.createElement("div")
    titleEl.textContent = title
    Object.assign(titleEl.style, {
      margin: "0 0 3px 0",
      fontSize: "14px",
      fontWeight: "700",
      color: colors.text,
      lineHeight: "1.35",
      letterSpacing: "-0.2px",
    })
    content.appendChild(titleEl)

    // --- Address ---
    const addressEl = document.createElement("div")
    addressEl.textContent = address
    Object.assign(addressEl.style, {
      margin: "0 0 0 0",
      fontSize: "12.5px",
      color: colors.textDim,
      lineHeight: "1.4",
    })
    content.appendChild(addressEl)

    // --- Opening hours (structured, line by line) ---
    if (openingHours) {
      const lines = formatOpeningHoursLines(openingHours)

      const hoursBox = document.createElement("div")
      Object.assign(hoursBox.style, {
        marginTop: "10px",
        padding: "8px 10px",
        backgroundColor: colors.surfaceSecondary,
        borderRadius: "8px",
      })

      // Hours header row with icon
      const headerRow = document.createElement("div")
      Object.assign(headerRow.style, {
        display: "flex",
        alignItems: "center",
        gap: "5px",
        marginBottom: "5px",
      })

      const clockSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
      clockSvg.setAttribute("width", "13")
      clockSvg.setAttribute("height", "13")
      clockSvg.setAttribute("viewBox", "0 0 24 24")
      clockSvg.setAttribute("fill", colors.textDim)
      const clockPath = document.createElementNS("http://www.w3.org/2000/svg", "path")
      clockPath.setAttribute(
        "d",
        "M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z",
      )
      clockSvg.appendChild(clockPath)
      headerRow.appendChild(clockSvg)

      const headerLabel = document.createElement("span")
      headerLabel.textContent = "Öffnungszeiten"
      Object.assign(headerLabel.style, {
        fontSize: "11px",
        fontWeight: "600",
        color: colors.textDim,
        letterSpacing: "0.2px",
      })
      headerRow.appendChild(headerLabel)
      hoursBox.appendChild(headerRow)

      // Day rows
      for (const line of lines) {
        const row = document.createElement("div")
        Object.assign(row.style, {
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "1.5px 0",
        })

        const dayLabel = document.createElement("span")
        dayLabel.textContent = line.label
        Object.assign(dayLabel.style, {
          fontSize: "11.5px",
          fontWeight: "500",
          color: colors.text,
          minWidth: "42px",
        })
        row.appendChild(dayLabel)

        const hoursLabel = document.createElement("span")
        hoursLabel.textContent = line.hours
        Object.assign(hoursLabel.style, {
          fontSize: "11.5px",
          color: colors.textDim,
          fontVariantNumeric: "tabular-nums",
        })
        row.appendChild(hoursLabel)

        hoursBox.appendChild(row)
      }

      content.appendChild(hoursBox)
    }

    // --- Maps button ---
    const btnWrapper = document.createElement("div")
    btnWrapper.style.marginTop = "10px"

    const btnEl = document.createElement("a")
    btnEl.href = mapsUrl
    btnEl.target = "_blank"
    btnEl.rel = "noopener noreferrer"
    btnEl.className = "hdp-maps-btn"
    Object.assign(btnEl.style, {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "6px",
      width: "100%",
      padding: "8px 12px",
      backgroundColor: colors.background,
      color: colors.surface,
      borderRadius: "10px",
      fontSize: "12.5px",
      fontWeight: "600",
      fontFamily: "OverusedGrotesk, -apple-system, BlinkMacSystemFont, sans-serif",
      textDecoration: "none",
      letterSpacing: "0.15px",
      boxSizing: "border-box",
      transition: "opacity 0.15s ease",
      cursor: "pointer",
    })

    // Pin SVG icon
    const pinSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
    pinSvg.setAttribute("width", "13")
    pinSvg.setAttribute("height", "13")
    pinSvg.setAttribute("viewBox", "0 0 24 24")
    pinSvg.setAttribute("fill", colors.surface)
    const pinPath = document.createElementNS("http://www.w3.org/2000/svg", "path")
    pinPath.setAttribute(
      "d",
      "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
    )
    pinSvg.appendChild(pinPath)
    btnEl.appendChild(pinSvg)
    btnEl.appendChild(document.createTextNode("In Maps öffnen"))

    btnWrapper.appendChild(btnEl)
    content.appendChild(btnWrapper)

    // --- Create info window ---
    const infoWindow = new google.maps.InfoWindow({
      content,
      position,
    })

    infoWindow.open(map)
    infoWindowRef.current = infoWindow

    google.maps.event.addListener(infoWindow, "closeclick", onClose)

    return () => {
      if (infoWindowRef.current) {
        infoWindowRef.current.close()
        infoWindowRef.current = null
      }
    }
  }, [map, position, title, address, onClose, openingHours, mapsUrlProp])

  return null
}
